import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Mail, Phone, MapPin, Send, Building2, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing again
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }

      setSubmitted(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        organisation: '',
        service: '',
        message: ''
      });
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message || 'Something went wrong. Please try again or email us directly at info@resiligent.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact RESILIGENT Lagos | Leadership Consultants Nigeria"
        description="Contact RESILIGENT for leadership development, executive coaching, and organisational development in Nigeria. Lagos office serving Nigeria, Ghana, Kenya, and across Africa. Request a consultation or briefing today."
        keywords="contact leadership consultant Nigeria, executive coaching enquiry Lagos, leadership development consultation Nigeria, HR consulting contact Lagos, organisational development Nigeria contact"
        canonical="/contact"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-[#0a0a0a] pt-32 pb-16">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                Get in Touch
              </h1>
              <p className="text-[#2693d0] text-lg lg:text-xl font-medium leading-relaxed mb-4">
                Let's discuss how we can support your organisation
              </p>
              <p className="text-slate-400 text-base lg:text-lg leading-relaxed">
                Whether you're exploring leadership diagnostics, coaching, assessment centres, or organisational development, we're here to help.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="bg-white py-16 lg:py-24">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              
              {/* Contact Form */}
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl lg:text-3xl mb-6">
                  Send us a message
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-8">
                  Tell us about your organisation and what you're looking to achieve. We'll get back to you within 24 hours.
                </p>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-green-800 font-semibold text-xl mb-2">Message Sent Successfully</h3>
                    <p className="text-green-700">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 text-[#2693d0] font-medium hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-800 font-medium">Unable to send message</p>
                          <p className="text-red-600 text-sm mt-1">{error}</p>
                        </div>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-slate-700 text-sm font-medium mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-slate-700 text-sm font-medium mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="organisation" className="block text-slate-700 text-sm font-medium mb-2">
                        Organisation
                      </label>
                      <input
                        type="text"
                        id="organisation"
                        name="organisation"
                        value={formData.organisation}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                        placeholder="Your company name"
                      />
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-slate-700 text-sm font-medium mb-2">
                        Area of Interest
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all bg-white"
                      >
                        <option value="">Select a service</option>
                        <option value="Diagnostics & 360 Assessments">Diagnostics & 360 Assessments</option>
                        <option value="Executive Coaching">Executive Coaching</option>
                        <option value="Leadership Development">Leadership Development</option>
                        <option value="Assessment Centres">Assessment Centres</option>
                        <option value="Graduate Recruitment">Graduate Recruitment</option>
                        <option value="Organisational Advisory">Organisational Advisory</option>
                        <option value="Impact AS Plus">Impact AS Plus™</option>
                        <option value="General Enquiry">General Enquiry</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-slate-700 text-sm font-medium mb-2">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all resize-none"
                        placeholder="Tell us about your organisation and what you're looking to achieve..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded-lg font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send size={18} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl lg:text-3xl mb-6">
                  Contact Information
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-8">
                  Reach out directly via email or phone, or visit one of our offices.
                </p>

                {/* Email */}
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#2693d0]/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#2693d0]" />
                    </div>
                    <h3 className="text-slate-800 font-semibold text-lg">Email</h3>
                  </div>
                  <a 
                    href="mailto:info@resiligent.com" 
                    className="text-[#2693d0] text-lg font-medium hover:underline"
                  >
                    info@resiligent.com
                  </a>
                </div>

                {/* West Africa Office */}
                <div className="mb-10 p-6 bg-[#f0f5f8] rounded-xl border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#2693d0]/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#2693d0]" />
                    </div>
                    <h3 className="text-slate-800 font-semibold text-lg">West Africa</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <p className="text-slate-600 text-base leading-relaxed">
                        154 Prince Ade Odedina Street<br />
                        Victoria Island<br />
                        Lagos, Nigeria
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <a 
                        href="tel:+2347030090712" 
                        className="text-slate-700 text-base hover:text-[#2693d0] transition-colors"
                      >
                        +234 (0)703 009 0712
                      </a>
                    </div>
                  </div>
                </div>

                {/* Europe Office */}
                <div className="p-6 bg-[#f0f5f8] rounded-xl border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#2693d0]/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#2693d0]" />
                    </div>
                    <h3 className="text-slate-800 font-semibold text-lg">Europe</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <p className="text-slate-600 text-base leading-relaxed">
                        Monomark House<br />
                        27 Old Gloucester Street<br />
                        WC1N 3AX, London<br />
                        United Kingdom
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <a 
                        href="tel:+442071176307" 
                        className="text-slate-700 text-base hover:text-[#2693d0] transition-colors"
                      >
                        +44 (0)207 117 630 7
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default ContactPage;
